<template>
  <page layoutClass="analytics-applications" :loading="!applications" :err="fatalErr">
    <div v-if="!hasReferralsEnabled && !isInternalMobilityEnabled" class="alert alert-warning">
      <b-row>
        <b-col cols="12" class="d-flex justify-content-center align-items-center">
          <p>
            <b>The Referrals feature is not enabled in your platform.</b>
          </p>
        </b-col>
        <b-col v-if="isRecruiterAdmin" cols="12" class="d-flex justify-content-center align-items-center">
          <b-button variant="primary" href="mailto:sales@reallinks.io?subject=Enabling Referrals in our platform">Contact Sales to enable this feature</b-button>
        </b-col>
      </b-row>
    </div>
    <div v-else>
    <b-row>
      <b-col cols="12">
        <div class="d-flex flex-column flex-md-row justify-content-md-between align-items-md-start my-3">
          <div class="d-flex flex-md-fill flex-column">
            <h4 class="text-uppercase m-0">Applications</h4>
            <!-- <p class="m-md-0">Job applications tracked on the Real Links platform</p> -->
          </div>
          <div class="d-flex flex-row justify-content-end align-items-center">
            <template v-if="applicationTypes.length > 1">
              <b-btn @click="applicationType = 'ALL'" size="sm" variant="link"
                :class="{'text-muted': applicationType === 'ALL', 'text-primary': applicationType !== 'ALL'}">
                  All
              </b-btn>
              <b-btn v-if="applicationTypes.includes('INTERNAL')" @click="applicationType = 'INTERNAL'" size="sm" variant="link"
                :class="{'text-muted': applicationType === 'INTERNAL', 'text-primary': applicationType !== 'INTERNAL'}">
                Internal
              </b-btn>
              <b-btn v-if="applicationTypes.includes('REFERRAL')" @click="applicationType = 'REFERRAL'" size="sm" variant="link"
                :class="{'text-muted': applicationType === 'REFERRAL', 'text-primary': applicationType !== 'REFERRAL'}">
                Referral
              </b-btn>
            </template>

            <b-dropdown class="ml-1" id="download-btn" no-caret right size="sm" variant="outline-primary">
              <template v-slot:button-content><font-awesome-icon icon="download"/><span class="ml-2 d-none d-sm-inline">Export</span></template>
              <b-dropdown-item @click="() => exportTableAs('csv')">CSV (.csv)</b-dropdown-item>
              <b-dropdown-item @click="() => exportTableAs('xlsx')">Excel (.xlsx)</b-dropdown-item>
            </b-dropdown>
            <b-btn class="ml-2" to="/jobs/applications/new" alt="Add new application" size="sm" variant="outline-primary">
              <font-awesome-icon icon="plus"/><span class="ml-2 d-none d-sm-inline">Add New</span>
            </b-btn>
            <b-btn class="ml-2" v-b-modal.greenhouse-assign alt="Import from Greenhouse" size="sm" variant="outline-primary" v-if="displayOldGHImport">
              <font-awesome-icon icon="plus"/><span class="ml-2 d-none d-sm-inline">Greenhouse Import</span>
            </b-btn>
            <b-btn class="ml-2" v-b-modal.greenhouse-import alt="Import from Greenhouse" size="sm" variant="outline-primary" v-if="displayNewGHImport">
              <font-awesome-icon icon="plus"/><span class="ml-2 d-none d-sm-inline">Greenhouse Import</span>
            </b-btn>
            <b-btn class="ml-2" v-b-modal.earcu-import alt="Import from eArcu" size="sm" variant="outline-primary" v-if="displayEArcuImport">
              <font-awesome-icon icon="plus"/><span class="ml-2 d-none d-sm-inline">eArcu Import</span>
            </b-btn>
          </div>
        </div>

        <!-- Search Bar -->
        <div>
          <b-form-group>
            <b-form-input
              id="title" placeholder="Search Candidate or Referee User" v-model="searchTerm"
            />
          </b-form-group>
          <!-- <b-btn @click="testLog()">Console</b-btn> -->
        </div>

        <b-table striped hover stacked="md" :items="applications" :fields="fields">

          <template #cell(fullName)="{ value, item }">
            <div class="d-flex flex-row justify-content-between">
              <span v-if="item.isInternal !== true">{{ value }}</span>
              <b-link
                v-else
                @click="selectedApplication = item"
                v-b-modal="'profile-show-modal'"
              >{{ value }}</b-link>
              <div>
                <a v-if="item.resume && item.resume.fileLocation" :href="item.resume.fileLocation" target="_blank">
                  <font-awesome-icon icon="file-alt" class="ml-2 text-secondary" />
                </a>
              </div>
            </div>
          </template>

          <template #cell(user)="{ value, item, toggleDetails }">
            <div class="d-flex flex-row justify-content-between" v-if="value && value.fullName">
              <span>{{ value.fullName }}</span>
              <div @click="toggleDetails">
                <font-awesome-icon v-if="item.userAnswers && item.userAnswers.message" icon="comments" class="ml-2 text-secondary"/>
              </div>
            </div>
            <template v-else-if="item && item.extraData && item.extraData.referreeName">
              {{ item.extraData.referreeName }}
            </template>
            <template v-else>—</template>
          </template>

          <template #cell(job)="{ value, item }">
            <strong v-if="item && item.atsData && item.atsData.jobTitle">
              {{ item.atsData.jobTitle }}
            </strong>
            <router-link v-else-if="value && value.id" :to="{ name: 'JobShow', params: { jobID: value.id } }">
              <strong>{{ value.title }}</strong>
            </router-link>
          </template>

          <template #cell(opts)="row">
            <div class="table-action-buttons">
              <b-button size="sm" @click="row.toggleDetails">
                <font-awesome-icon v-if="!row.detailsShowing" icon="chevron-down"/>
                <font-awesome-icon v-else icon="chevron-up"/>
              </b-button>
              <b-button size="sm" class="ml-3" @click="openEditModal(row.item.id)">
                <font-awesome-icon icon="pencil-alt"/>
              </b-button>
            </div>
          </template>

          <template #row-details="{ item }">
            <b-card>
              <b-row>
                <b-col cols="12" sm="6" v-for="(column, i) in rowDetails" :key="`rowDetails-${i}`">
                  <b-row class="mb-2" v-for="({ label, key, formatter }) in column" :key="`rowDetails-${i}-${key}`">
                    <template v-if="key === 'atsData'">
                      <b-col cols="12">
                        <strong>{{ label }}:</strong>
                      </b-col>
                      <template v-for="(dataValue, dataKey) in (item[key] || {})">
                        <b-col cols="4" class="pl-4" :key="`label-${dataKey}`"><strong>{{ _startCase(dataKey) }}:</strong></b-col>
                        <b-col cols="8" :key="`value-${dataKey}`">{{ dataValue || '-' }}</b-col>
                      </template>
                    </template>
                    <template v-else-if="key === 'customQuestionAnswers' && item[key]">
                      <b-col cols="12">
                        <strong>{{ label }}:</strong>
                        <advanced-form disabled layout="inline-md-4" v-bind="item[key]">
                          <template v-slot:formControl></template>
                        </advanced-form>
                      </b-col>
                    </template>
                    <template v-else>
                      <b-col cols="4">
                        <strong>{{ label || _startCase(key) }}:</strong>
                      </b-col>
                      <b-col col :class="{ 'text-truncate': [ 'mailto', 'url' ].includes(formatter) }">
                        <template v-if="[ 'resume', 'coverLetter' ].includes(key)">
                          <template v-if="item[key] && item[key].fileLocation">
                            <a :href="item[key].fileLocation" target="_blank">{{ item[key].fileName || item[key].fileLocation }}</a>
                            <template v-if="item[key].fileSize">({{ filesizeToMb(item[key].fileSize) }})</template>
                          </template>
                          <template v-else>-</template>
                        </template>
                        <template v-else-if="formatter === 'url'">
                          <a v-if="_get(item, key, null)" :href="_get(item, key, '#')" target="_blank">
                            {{ `${_get(item, key, '')}`.replace(/^https?:\/\/(www.)?/, '') }}
                          </a>
                          <template v-else>-</template>
                        </template>
                        <template v-else-if="formatter === 'mailto'">
                          <a v-if="_get(item, key, null)" :href="`mailto:${_get(item, key, '#')}`" target="_blank">
                            {{ _get(item, key, '-') }}
                          </a>
                          <template v-else>-</template>
                        </template>
                        <template v-else-if="typeof formatter === 'function'">
                          {{ formatter(_get(item, key, null)) || '-' }}
                        </template>
                        <template v-else>
                          {{ _get(item, key, '-') }}
                        </template>
                      </b-col>
                    </template>
                  </b-row>
                </b-col>
              </b-row>
            </b-card>
          </template>

        </b-table>

        <b-pagination v-if="Array.isArray(applications) && applications.length" v-model="page" :total-rows="total" :per-page="limit" align="center"/>
      </b-col>
    </b-row>

    <!-- User Profile Modal -->
    <b-modal
      id="profile-show-modal"
      title="Profile"
      body-class="gray-bg"
      hide-footer
      lazy
    >
      <profile-show
        :user="selectedApplication ? selectedApplication.user : null"
        :message="selectedApplication ? selectedApplication.message : null"
        layout="modal"
      />
    </b-modal>

    <b-modal ref="edit-modal" title="Edit Application" v-if="selectedID">
      <div class="my-2">
        <label>Job</label>
        <p v-if="$apollo.queries.jobSearch.loading">Loading..</p>
        <multiselect v-else
          class="multiselect-rlnx" v-model="selectedJob"
          label="title" track-by="id" placeholder="Type to search" open-direction="bottom"
          :options="jobSearch" :searchable="true"
          :loading="$apollo.queries.jobSearch.loading" :limit="3" :max-height="600"
          @search-change="value => jobSearchQuery = value"
          :class="{ 'is-invalid': typeof isValid.selectedJob === 'string' }">
          <template v-slot:option="{ option }">
            {{ option.title }}
            <template v-if="option.location && option.location.name">({{ option.location.name }})</template>
          </template>
          <template v-slot:noResult>No jobs found - consider changing your search query</template>
        </multiselect>
      </div>
      <div class="my-2">
        <label>Referee User</label>
        <p v-if="$apollo.queries.allUsers.loading">Loading..</p>
        <multiselect v-else
          v-model="selectedUser"
          :options="allUsers"
          placeholder="Select one or leave blank"
          label="fullName"
          track-by="id"
          class="multiselect-rlnx">
          <template v-slot:noResult>Not found - has the employee signed up to the platform yet?</template>
        </multiselect>
      </div>
      <div class="my-2">
        <label for="statusOptions">Status</label>
        <b-form-select v-model="form.status" :options="statusOptions" placeholder="please select">
          <!-- <option value="null">Please select an option</option> -->
        </b-form-select>
      </div>
      <template v-slot:modal-footer>
        <div class="w-100">
          <b-button variant="secondary" size="sm" class="float-left" @click="closeEditModal()">
            Close
          </b-button>
          <b-button v-if="selectedID" variant="primary" size="sm" class="float-right" @click="updateApplication()">
            Update
          </b-button>
        </div>
      </template>
    </b-modal>

    <b-modal id="greenhouse-assign" title="Import application from Greenhouse" hide-footer size="lg">
      <apply-assign @submit="assignApplication" />
    </b-modal>

    <b-modal id="greenhouse-import" title="Import application from Greenhouse" hide-footer size="lg">
      <apply-import @submit="importApplication" />
    </b-modal>

    <b-modal id="earcu-import" title="Import application from eArcu" hide-footer size="lg">
      <apply-e-arcu-import @submit="importApplication" />
    </b-modal>
    </div>
  </page>
</template>

<script>
import _get from 'lodash/get';
import _startCase from 'lodash/startCase';
import bytes from 'bytes';
import formatDate from 'date-fns/format';
import Page from '@/components/Page';
import ProfileShow from '@/components/profile/ProfileShow';
import JobApplicationsQuery from '@/graphql/queries/JobApplications.gql';
import JobApplicationsMutation from '@/graphql/mutations/JobApplicationUpdate-Recruiter.gql';
import JobSearchQuery from '@/graphql/queries/JobSearch.gql';
import AdvancedForm from '@/components/AdvancedForm';
import ms from 'ms';
import ExcelJS from 'exceljs';
import Multiselect from 'vue-multiselect';
import ApplyAssign from '@/components/jobs/applyAssign';
import ApplyImport from '@/components/jobs/applyImport';
import ApplyEArcuImport from '@/components/jobs/applyEArcuImport';
import UsersIndexQuery from '@/graphql/queries/UsersList.gql';
import { mapRoot, mapPreferences } from '@/mixins/root';

export default {
  components: {
    Page,
    AdvancedForm,
    Multiselect,
    ProfileShow,
    ApplyAssign,
    ApplyImport,
    ApplyEArcuImport
  },
  data: () => ({
    searchTerm: null,
    fatalErr: null,
    selectedID: null,
    applications: null,
    gridAPI: null,
    selectedJob: null,
    selectedUser: null,
    jobSearchQuery: null,
    selectedApplication: null,
    form: {
      status: null,
      job: {
        id: null
      },
      user: {
        id: null
      }
    },
    selected: null,
    page: 1,
    limit: 20,
    total: 0,
    statusOptions: [
      'CREATED',
      'CONFIRMED',
      'FORWARDED',
      'INTERVIEW',
      'HIRED',
      'REJECTED',
      'DELETED'
    ],
    applicationType: 'ALL',
  }),
  apollo: {
    applications: {
      query: JobApplicationsQuery,
      variables() {
        const { applicationType, page, limit, searchTerm } = this;
        let searchFilter = {};
        if (searchTerm && searchTerm.length > 1) {
          searchFilter = {
            OR: [
              {
                user: {
                  name: searchTerm
                },
              },
              {
                name: searchTerm,
              }
            ]
          };
        }
        const filters = {
          where: {
            NOT: {
              status: 'DELETED',
            },
            ...searchFilter,
          },
          page,
          limit,
          order: 'CREATED_DESC'
        };
        if (applicationType === 'INTERNAL') {
          filters.where.isInternal = true;
        }
        if (applicationType === 'REFERRAL') {
          filters.where.isInternal = false;
        }
        return filters;
      },
      update({ company: { applicationConnections } }) {
        // const excludeQuestions = [ 'veteran_status', 'gender', 'race', 'disability_status' ];
        const { edges } = applicationConnections || {};
        return (Array.isArray(edges) ? edges : []).map(({ entry: application }) => {
          if (!application) return null;
          if (Array.isArray(application.customQuestions) && application.customQuestions.length) {
            const { customQuestions, customAnswers } = application;
            console.log({ customAnswers, customQuestions });
            application.customQuestionAnswers = {
              form: customQuestions
                .filter(({ setID }) => !(setID || '').toLowerCase().includes('demographic') && !(setID || '').toLowerCase().includes('compliance'))
                .reduce((qs, { id, ...field }) => ({ ...qs, [`custom-${id}`]: field }), {}),
              value: Array.isArray(customAnswers)
                ? customAnswers.reduce((as, { questionID: id, value, values, file }) => ({
                  ...as,
                  [`custom-${id}`]: (file && file.id ? file : undefined) || (Array.isArray(values) ? values : undefined) || value,
                }), {})
                : null,
            };
          }
          return application;
        }).filter(el => el);
      },
      result({ loading, data }) {
        if (!loading && data) {
          const { company } = data;
          const { applicationConnections } = company || {};
          const { page, total } = applicationConnections || {};
          this.page = page || 1;
          this.total = total || 0;
        }
      },
      error(err) {
        console.error(err);
        this.fatalErr = err;
      },
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
      prefetch: false,
    },
    jobSearch: {
      query: JobSearchQuery,
      variables() {
        const { jobSearchQuery } = this;
        return { limit: 10, where: { title: jobSearchQuery } };
      },
      debounce: ms('1s'),
      update: ({ company: { jobs } }) => jobs,
      error(err) {
        this.err = `${err}`;
      },
      skip() {
        return !this.selectedID;
      },
    },
    allUsers: {
      query: UsersIndexQuery,
      update: ({ company: { userConnections } }) => (userConnections.edges || []).map(({ entry }) => entry),
      error(err) {
        this.err = `${err}`;
      },
      skip() {
        return !this.selectedID;
      },
    },
  },
  metaInfo() {
    const title = 'Applications';
    return { title };
  },
  computed: {
    ...mapRoot([ 'user', 'company', 'companyHandle', 'isReferralsFeatureEnabled', 'isInternalMobilityEnabled', 'isRecruiterAdmin' ]),
    ...mapPreferences([ 'displayOldGHImport', 'displayNewGHImport', 'displayEArcuImport' ]),
    fields() {
      const fields = [
        {
          headerTitle: 'Name',
          label: 'Candidate',
          key: 'fullName',
          formatter: (value, key, { id }) => value || `Candidate #${id}`,
        },
        {
          headerTitle: 'Job',
          label: 'Job',
          key: 'job',
        },
        {
          headerTitle: 'ATS ID',
          label: 'ATS ID',
          key: 'atsID',
          formatter: value => value || '—',
        },
        {
          headerTitle: 'Status',
          label: 'Status',
          key: 'status',
          formatter: value => _startCase(`${value}`.toLowerCase()),
        },
        {
          headerTitle: 'Created At',
          label: 'Created At',
          key: 'created',
          sortable: true,
          formatter: value => formatDate(value, 'DD/MM/YYYY')
        },
        {
          headerTitle: 'Updated At',
          label: 'Updated At',
          key: 'updated',
          sortable: true,
          formatter: value => formatDate(value, 'DD/MM/YYYY')
        },
        {
          headerTitle: 'Options',
          label: '',
          key: 'opts',
          tdClass: 'text-right',
        },
      ];

      if (this.isReferralsFeatureEnabled) {
        fields.splice(2, 0, {
          label: 'Referee User',
          key: 'user',
        });
      }

      if (this.applicationTypes.length > 1) {
        fields.splice(2, 0, {
          label: 'Type',
          key: 'isInternal',
          formatter: value => value ? 'Internal' : 'Referral'
        });
      }

      return fields;
    },
    rowDetails() {
      const referralDetails = this.isReferralsFeatureEnabled ? [
        { key: 'referral.recruiter.fullName', label: 'Recruiter (Name)' },
        { key: 'referral.recruiter.emailAddress', label: 'Recruiter (Email)' },
        { key: 'userAnswers.type', label: 'Referee Type', formatter: value => value ? _startCase(`${value}`.toLowerCase()) : null },
        { key: 'userAnswers.message', label: 'Referee Message' },
      ] : [];

      return {
        leftColumn: [
          { key: 'emailAddress', label: 'Email', formatter: 'mailto' },
          { key: 'phoneNumber', label: 'Phone' },
          { key: 'linkedInURL', label: 'LinkedIn Profile' },
          { key: 'location', label: 'Location' },
          { key: 'resume', label: 'Resume' },
          { key: 'coverLetter', label: 'Cover Letter' },
          ...referralDetails,
          { key: 'source' },
          { key: 'created', label: 'Created At', formatter: value => formatDate(value, 'DD/MM/YYYY HH:mm') },
          { key: 'updated', label: 'Updated At', formatter: value => formatDate(value, 'DD/MM/YYYY HH:mm') },
        ],
        rightColumn: [
          { key: 'atsID', label: 'ATS ID' },
          { key: 'atsStatus', label: 'ATS Status' },
          { key: 'atsURL', label: 'ATS URL', formatter: 'url' },
          { key: 'atsData', label: 'ATS Data' },
          { key: 'atsError.message', label: 'ATS Error' },
          { key: 'customQuestionAnswers', label: 'Custom Questions' },
        ],
      };
    },
    exportFields: () => ([
      {
        key: 'fullName',
        label: 'Name',
        formatter: (value, key, { id }) => value || `Candidate #${id}`,
      },
      {
        key: 'job.title',
        label: 'Job',
      },
      {
        key: 'user.fullName',
        label: 'Referee Name',
      },
      {
        key: 'userAnswers.type',
        label: 'Referee Type',
      },
      {
        key: 'userAnswers.message',
        label: 'Referee Message',
      },
      {
        key: 'emailAddress',
        label: 'Email',
      },
      {
        key: 'phoneNumber',
        label: 'Phone',
      },
      {
        key: 'resume.fileLocation',
        label: 'Resume',
      },
      {
        key: 'coverLetter.fileLocation',
        label: 'Cover Letter',
      },
      {
        key: 'atsID',
        label: 'ATS ID',
      },
      {
        key: 'atsStatus',
        label: 'ATS Status',
      },
      {
        key: 'atsURL',
        label: 'ATS URL',
      },
      {
        key: 'atsError.message',
        label: 'ATS Error',
      },
      {
        key: 'source',
        label: 'Source',
      },
      {
        key: 'status',
        label: 'Status',
      },
      {
        key: 'created',
        label: 'Created',
        formatter: value => formatDate(value, 'DD/MM/YYYY HH:mm'),
      },
      {
        key: 'updated',
        label: 'Updated',
        formatter: value => formatDate(value, 'DD/MM/YYYY HH:mm'),
      },
    ]),
    isValid() {
      const isValid = {};

      if (this.form.hasOwnProperty('jobID')) {
        if (this.form.job.id) {
          isValid.selectedJob = true;
        } else if (!this.form.job.id) {
          isValid.selectedJob = 'NOT_SELECTED';
        } else {
          isValid.selectedJob = null;
        }
      } else {
        isValid.selectedJob = null;
      }

      return isValid;
    },
    applicationTypes() {
      const types = [];

      if (this.isReferralsFeatureEnabled) {
        types.push('REFERRAL');
      }

      if (this.isInternalMobilityEnabled) {
        types.push('INTERNAL');
      }

      if (types.length > 1) {
        types.unshift('ALL');
      }

      return types;
    },
    isGreenhouseAts() {
      return (a => a.includes(this.companyHandle))([
        'optiver', 'optiver-apac', 'optiver-interns',
        'mindgym', 'paddle', 'capco', 'celonis',
      ]);
    },
    sitePreferences() {
      const { sitePreferences } = this.company || {};
      return sitePreferences || {};
    },
    hasReferralsEnabled() {
      const { featuresEnabled } = this.sitePreferences || {};
      return (featuresEnabled || []).includes('referrals');
    },
  },
  methods: {
    _get: (...args) => _get(...args),
    _startCase: string => _startCase(string),
    filesizeToMb: num => bytes(parseInt(num, 10) || 0, { decimalPlaces: 1 }),
    openEditModal(selectedID) {
      const { status, job, user } = this.applications.find(item => item.id === selectedID);
      this.selectedID = selectedID;
      this.form.status = status;
      this.selectedJob = job;
      this.selectedUser = user;
      this.$nextTick(() => this.$refs['edit-modal'].show());
    },
    closeEditModal() {
      this.selectedID = null;
      this.form.status = null;
      this.selectedJob = null;
      this.selectedUser = null;
      this.$nextTick(() => this.$refs['edit-modal'].hide());
    },
    async updateApplication() {
      try {
        if (!this.selectedJob || !this.selectedJob.id) {
          throw new Error('Job is not selected');
        }
        this.form.job.id = this.selectedJob.id;
        this.form.user = this.selectedUser ? { id: this.selectedUser.id } : null;
        await this.$apollo.mutate({
          mutation: JobApplicationsMutation,
          variables: { applicationIDs: [ this.selectedID ], update: this.form },
        });
        this.closeEditModal();
      } catch (err) {
        this.$notifyErr(err, { prepend: 'Failed to update application:' });
        this.$log.error(err);
        this.closeEditModal();
      }
    },
    async exportTableAs(type) {
      try {
        this.$assert([ 'csv', 'xlsx' ].includes(type), new Error('Invalid file export type chosen'));

        const result = await this.$apollo.query({
          // Query
          query: JobApplicationsQuery,
          // Parameters
          variables: {
            where: {
              NOT: {
                status: 'DELETED',
              },
            },
            page: 1,
            limit: 2000,
            order: 'CREATED_DESC'
          },
        });

        const { company } = result.data;
        const { applicationConnections } = company || {};
        const { edges } = applicationConnections || {};
        const applications = (edges || []).map(({ entry: application }) => application || null).filter(el => el);

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Table Export');

        worksheet.columns = this.exportFields.map((field, i) => {
          this.$assert(field && field.key, new Error(`Missing exportFields[${i}].key`));

          return {
            header: `${field.label || _startCase(field.key)}`,
            key: `field-${i}`,
          };
        });

        worksheet.addRows(applications.map(item => {
          const row = {};

          this.exportFields.forEach((field, i) => {
            let value = _get(item, field.key, null);

            if (typeof field.formatter === 'function') {
              value = field.formatter(value, field.key, item);
            }

            row[`field-${i}`] = value;
          });

          return row;
        }));

        const formatOpts = type === 'xlsx'
          ? {}
          : {
            transform(row) {
              return row.map(cell => {
                if (typeof cell === 'string') {
                  if ([ '=', '+', '-', '@' ].includes(cell.substr(0, 1))) {
                    cell = `'${cell}`;
                  }
                }

                return cell;
              });
            },
          };

        const buffer = await workbook[type].writeBuffer(formatOpts);
        const blob = new Blob([ buffer ], { type: type === 'xlsx' ? 'application/vnd.ms-excel' : 'text/csv' });

        const link = document.createElement('a');
        link.download = `Real-Links-${this.company.name}-Applications-${formatDate(new Date(), 'YYYY-MM-DD')}.${type}`;
        link.target = 'blank';
        link.href = URL.createObjectURL(blob);
        link.click();
      } catch (err) {
        this.$notifyErr(err, { prepend: 'Failed to export table:' });
      }
    },
    openInternalUserModal(user) {
      console.log('USER: ', user);
      console.log('COMPANY: ', this.company);
    },
    assignApplication(application) {
      console.log('ASSIGN APPLICATION: ', application);
    },
  },
};
</script>

<style lang="scss">
.analytics-applications {
  .top-action-buttons {
    min-width: 100px;
  }
  .table-action-buttons {
    min-width: 100px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/Recruiter/ListApplications.vue